import { store } from '../redux/store';
import { resetRedux, customerReset, referralReset, notifySet } from '../redux/actions';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const AUTH_TOKEN_KEY = 'sa_access_token';
const UNAUTHORIZED_MESSAGE = 'Unauthorized access. Please log in.';

export const setAuthToken = (token) => {
  if (token) {
    sessionStorage.setItem(AUTH_TOKEN_KEY, token);
  } else {
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
  }
};

export const getAuthToken = () => sessionStorage.getItem(AUTH_TOKEN_KEY);

export const clearAuthToken = () => sessionStorage.removeItem(AUTH_TOKEN_KEY);

const buildUrl = (functionName) => {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL is not configured');
  }
  return `${API_BASE_URL.replace(/\/$/, '')}/${functionName}`;
};

let isHandlingUnauthorized = false;

const isUnauthorizedMessage = (message) => typeof message === 'string' && /unauthor/i.test(message);

const isUnauthorizedResponse = (httpStatus, payload) => {
  if (httpStatus === 401 || httpStatus === 403) return true;
  if (payload?.result && payload.result.status === false) {
    return isUnauthorizedMessage(payload.result.message);
  }
  if (payload?.error) {
    return isUnauthorizedMessage(payload.error.message);
  }
  return false;
};

const handleUnauthorized = (message) => {
  if (isHandlingUnauthorized) return;
  isHandlingUnauthorized = true;

  // Defer cleanup to a macrotask so any sequentially-awaited calls already
  // queued (e.g. getRooftop -> getRooftopConfig in Login flow) still read the
  // token from sessionStorage and send the X-Magic-Token header. The redirect
  // to /login then runs once the current call chain has settled.
  setTimeout(() => {
    clearAuthToken();
    try {
      sessionStorage.clear();
      store.dispatch(resetRedux());
      store.dispatch(customerReset());
      store.dispatch(referralReset());
      store.dispatch(notifySet({ type: 'error', message: message || UNAUTHORIZED_MESSAGE }));
    } catch (e) {
      // store may not be initialized yet; redirect anyway
    }

    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  }, 0);
};

export const callFBFunction = async (functionName, data) => {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  const requestData =
    token && data && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, access_token: token }
      : token
      ? { ...(data || {}), access_token: token }
      : data ?? null;

  const response = await fetch(buildUrl(functionName), {
    method: 'POST',
    headers,
    body: JSON.stringify({ data: requestData })
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch (e) {
    payload = null;
  }

  if (isUnauthorizedResponse(response.status, payload)) {
    const message = payload?.result?.message || payload?.error?.message || UNAUTHORIZED_MESSAGE;
    handleUnauthorized(message);
    const error = new Error(message);
    error.code = 'unauthenticated';
    error.httpStatus = response.status;
    throw error;
  }

  if (!response.ok || (payload && payload.error)) {
    const errInfo = (payload && payload.error) || { message: response.statusText };
    const error = new Error(errInfo.message || 'Request failed');
    error.code = errInfo.status;
    error.details = errInfo.details;
    error.httpStatus = response.status;
    throw error;
  }

  return { data: payload ? payload.result : undefined };
};

export const getAllDealerInventoryByRooftop = async (filter, order) => {
  try {
    const response = await callFBFunction('access', {
      type: 'getAllDealerInventoryByRooftop',
      payload: { filter, order }
    });
    return response?.data?.data ? response.data.data : [];
  } catch (e) {
    return false;
  }
};

export const getProfile = async () => {
  try {
    const response = await callFBFunction('access', {
      type: 'getProfile'
    });
    return response?.data?.data ? response.data.data : [];
  } catch (e) {
    return false;
  }
};

export const updateProfile = async (data) => {
  try {
    const response = await callFBFunction('access', {
      type: 'updateProfile',
      payload: { data }
    });
    return response?.data?.data ? response.data.data : {};
  } catch (e) {
    return false;
  }
};

export const getRooftop = async () => {
  try {
    const response = await callFBFunction('access', {
      type: 'getRooftop'
    });
    return response?.data?.data ? response.data.data : [];
  } catch (e) {
    return false;
  }
};

export const getRooftopConfig = async () => {
  try {
    const response = await callFBFunction('access', {
      type: 'getRooftopConfig'
    });
    return response?.data?.data ? response.data.data : [];
  } catch (e) {
    return false;
  }
};

export const uploadFile = async (path, fileData) => {
  const contentType = fileData.type;
  const fileExtention = fileData.name.split('.').pop();

  const signedUrlRes = await callFBFunction('generateSignedURL', {
    path,
    contentType,
    fileExtention
  });

  if (!signedUrlRes?.data?.data) {
    throw new Error('Failed to generate signed URL');
  }

  const { url, destination } = signedUrlRes.data.data;

  const upload = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType
    },
    body: fileData
  });

  if (!upload.ok) {
    throw new Error('Failed to upload file to storage');
  }

  const downloadURLRes = await callFBFunction('generateDownloadURL', { path: destination });

  if (downloadURLRes?.data && downloadURLRes.data.status === false) {
    throw new Error(downloadURLRes.data.message || 'Failed to generate download URL');
  }

  if (!downloadURLRes?.data?.data) {
    throw new Error('Failed to retrieve download URL');
  }

  return downloadURLRes.data.data;
};

export const deleteFile = (path) =>
  new Promise((resolve, reject) => {
    try {
      const match = path.replaceAll('%2F', '/').split('/').slice(4).join('/');

      callFBFunction('deleteItem', { path: `${match}` }).then((data) => {
        if (data.data.status) {
          resolve(true);
        }
        reject(data.data.message);
      });
    } catch (error) {
      reject(error);
    }
  });

export const deleteInventoryById = (id) =>
  new Promise((resolve, reject) => {
    try {
      callFBFunction('access', {
        type: 'deleteInventoryById',
        payload: { id }
      }).then((data) => {
        if (data.data.status) {
          resolve(true);
        }
        reject(data.data.message);
      });
    } catch (error) {
      reject(error);
    }
  });
